import * as React from 'react';
import { ScrollText } from 'lucide-react';
import { EmptyState } from '../components/EmptyState.js';
import {
  HeaderFilter,
  HeaderLabel,
  TableCard,
  TableHeaderRow,
  TableLoading,
  TableMessage,
  TablePagination,
} from '../components/table/index.js';
import { AppShell } from '../components/layout/AppShell.js';
import { cn } from '../lib/utils.js';

// A single audit entry: who did what, to which record, and when.
export interface SystemLog {
  _id: string;
  createdAt: string;
  level: 'info' | 'warning' | 'error';
  /** Short machine-ish action name, e.g. "listing.sync". */
  action: string;
  message: string;
  actor: string | null;
  target: string | null;
}

export interface SystemLogListResponse {
  items: SystemLog[];
  meta: {
    total: number;
    page: number;
    limit: number;
    pages: number;
    counts: { all: number; info: number; warning: number; error: number };
  };
}

type LevelFilter = 'all' | 'info' | 'warning' | 'error';

const LEVEL_OPTIONS: { value: LevelFilter; label: string }[] = [
  { value: 'all', label: 'All logs' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warnings' },
  { value: 'error', label: 'Errors' },
];

const EMPTY: SystemLogListResponse = {
  items: [],
  meta: {
    total: 0,
    page: 1,
    limit: 25,
    pages: 1,
    counts: { all: 0, info: 0, warning: 0, error: 0 },
  },
};

export function SystemLogsPage() {
  const [level, setLevel] = React.useState<LevelFilter>('all');
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);
  const [data, setData] = React.useState<SystemLogListResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshing, setRefreshing] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    setError(null);
    setRefreshing(true);
    const qs = new URLSearchParams({
      level,
      page: String(page),
      limit: String(pageSize),
    });
    void fetch(`/api/system-logs?${qs}`, { credentials: 'include' })
      .then(async (res) => {
        // No logs endpoint on the API yet — a 404 reads as "nothing
        // recorded" rather than an error the user can't act on.
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return (await res.json()) as SystemLogListResponse;
      })
      .then((res) => {
        if (alive) setData(res ?? EMPTY);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      })
      .finally(() => {
        if (alive) setRefreshing(false);
      });
    return () => {
      alive = false;
    };
  }, [level, page, pageSize]);

  const meta = data?.meta;
  const items = data?.items ?? null;
  const counts = meta?.counts;

  return (
    <AppShell>
      <div className="flex flex-1 flex-col space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              System logs
            </h1>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">
              {counts ? (
                <>
                  <span className="text-foreground">{counts.all}</span> entries
                  <span className="mx-2 text-muted-foreground/60">·</span>
                  <span className="text-foreground">{counts.warning}</span>{' '}
                  warnings
                  <span className="mx-2 text-muted-foreground/60">·</span>
                  <span className="text-foreground">{counts.error}</span> errors
                </>
              ) : (
                'Loading…'
              )}
            </p>
          </div>
        </div>

        <TableCard
          minWidth={1000}
          busy={refreshing && items !== null}
          footer={
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={meta?.total ?? 0}
              totalPages={meta?.pages ?? 1}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          }
        >
          <TableHeaderRow>
            <HeaderFilter
              className="min-w-[200px] flex-[1.4_1_200px]"
              value={level}
              options={LEVEL_OPTIONS}
              onChange={(v) => {
                setLevel(v);
                setPage(1);
              }}
              menuClassName="min-w-[10rem]"
            />
            <HeaderLabel className="min-w-[160px] flex-[1.4_1_160px]">
              Action
            </HeaderLabel>
            <HeaderLabel className="min-w-[300px] flex-[3_1_300px]">
              Message
            </HeaderLabel>
            <HeaderLabel className="min-w-[150px] flex-[1.4_1_150px]">
              Actor
            </HeaderLabel>
            <HeaderLabel className="min-w-[170px] flex-[1.5_1_170px]">
              When
            </HeaderLabel>
          </TableHeaderRow>

          {error ? (
            <TableMessage tone="destructive">{error}</TableMessage>
          ) : items === null ? (
            <TableLoading />
          ) : items.length === 0 ? (
            <EmptyState
              icon={ScrollText}
              title={level === 'all' ? 'No logs yet' : `No ${level} entries`}
              description="Syncs, imports, and other background activity are recorded here so you can see what the system has been doing."
            />
          ) : (
            items.map((log) => <LogRow key={log._id} log={log} />)
          )}
        </TableCard>
      </div>
    </AppShell>
  );
}

function LogRow({ log }: { log: SystemLog }) {
  return (
    <div className="flex items-center gap-4 border-b border-[hsl(var(--hairline))] px-5 py-3.5 transition-colors last:border-b-0 hover:bg-muted/60">
      <div className="min-w-[200px] flex-[1.4_1_200px]">
        <LevelPill level={log.level} />
      </div>
      <div className="min-w-[160px] flex-[1.4_1_160px] truncate font-mono text-[11px] text-foreground/80">
        {log.action}
      </div>
      <div className="min-w-[300px] flex-[3_1_300px] min-w-0">
        <div className="truncate font-sans text-[13px] text-foreground">
          {log.message}
        </div>
        {log.target && (
          <div className="truncate font-mono text-[10px] text-muted-foreground">
            {log.target}
          </div>
        )}
      </div>
      <div className="min-w-[150px] flex-[1.4_1_150px] truncate font-sans text-[13px] text-muted-foreground">
        {log.actor ?? 'System'}
      </div>
      <div className="min-w-[170px] flex-[1.5_1_170px] truncate font-mono text-[11px] tabular-nums text-muted-foreground">
        {formatWhen(log.createdAt)}
      </div>
    </div>
  );
}

function LevelPill({ level }: { level: SystemLog['level'] }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.06em]',
        level === 'error'
          ? 'border-destructive/30 bg-destructive/10 text-destructive'
          : level === 'warning'
            ? 'border-warning/40 bg-warning/10 text-warning'
            : 'border-border bg-muted/30 text-muted-foreground',
      )}
    >
      <span
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          level === 'error'
            ? 'bg-destructive'
            : level === 'warning'
              ? 'bg-warning'
              : 'bg-muted-foreground/60',
        )}
      />
      {level}
    </span>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
